import * as THREE from 'three';

class Wall {
  constructor(startPoint, endPoint, height = 2.7, thickness = 0.2) {
    this.id = this.generateId();
    this.start = new THREE.Vector3(startPoint.x, 0, startPoint.z);
    this.end = new THREE.Vector3(endPoint.x, 0, endPoint.z);
    // Posizioni canoniche (senza estensione angolo): rappresentano l'intenzione
    // dell'utente prima di qualsiasi allungamento per raccordi d'angolo
    // Quando la parete NON è agganciata ad un'altra, start/end coincidono con queste
    this.canonicalStart = new THREE.Vector3(startPoint.x, 0, startPoint.z);
    this.canonicalEnd   = new THREE.Vector3(endPoint.x, 0, endPoint.z);
    this.height = height;
    this.thickness = thickness;
    this.mesh = null;
    this.material = null;
    this.doors = [];
    this.windows = [];
  }
  
  generateId() {
    return 'wall_' + Math.random().toString(36).substr(2, 9);
  }
  
  getLength() {
    return this.start.distanceTo(this.end);
  }
  
  getCenter() {
    return new THREE.Vector3().addVectors(this.start, this.end).multiplyScalar(0.5);
  }
  
  getDirection() {
    return new THREE.Vector3().subVectors(this.end, this.start).normalize();
  }
  
  getAngle() {
    const dir = this.getDirection();
    return Math.atan2(dir.z, dir.x);
  }
  
  createMesh() {
    this.mesh = new THREE.Group();
    this.mesh.userData.wall = this;
    
    this.material = new THREE.MeshStandardMaterial({
      color: 0xeeeeee,
      roughness: 0.8,
      metalness: 0.2
    });
    
    this.rebuildWallSegments();
    
    return this.mesh;
  }
  
  rebuildWallSegments() {
    if (!this.mesh) return;
    
    // Rimuove tutti i segmenti esistenti
    while (this.mesh.children.length > 0) {
      const child = this.mesh.children[0];
      this.mesh.remove(child);
      if (child.geometry) child.geometry.dispose();
    }
    
    const wallLength = this.getLength();
    
    // Raccoglie tutte le aperture (porte e finestre)
    const openings = [];
    
    // Aggiunge porte
    if (this.doors) {
      this.doors.forEach(door => {
        const box = door.getBoundingBox();
        const frameThickness = 0.08;
        const marginNormalized = frameThickness / wallLength;
        
        openings.push({
          start: box.start - marginNormalized,
          end: box.end + marginNormalized,
          heightStart: 0,
          heightEnd: door.height + frameThickness,
          type: 'door'
        });
      });
    }
    
    // Aggiunge finestre
    if (this.windows) {
      this.windows.forEach(window => {
        const box = window.getBoundingBox();
        const frameThickness = 0.07; // Telaio PVC
        const sillDepth = 0.06;
        const marginNormalized = frameThickness / wallLength;
        
        openings.push({
          start: box.start - marginNormalized,
          end: box.end + marginNormalized,
          heightStart: box.verticalStart - sillDepth - 0.01,
          heightEnd: box.verticalEnd,
          type: 'window'
        });
      });
    }
    
    // Ordina le aperture per posizione
    openings.sort((a, b) => a.start - b.start);
    
    // Se non ci sono aperture, crea una parete intera
    if (openings.length === 0) {
      this.createWallSegment(0, 1, 0, this.height);
      return;
    }
    
    // Crea segmenti tra le aperture
    let currentPos = 0;
    
    openings.forEach(opening => {
      // Segmento prima dell'apertura
      if (opening.start > currentPos + 0.01) {
        this.createWallSegment(currentPos, opening.start, 0, this.height);
      }
      
      // Per le finestre, crea segmenti sopra e sotto
      if (opening.type === 'window') {
        // Segmento sotto la finestra
        if (opening.heightStart > 0.01) {
          this.createWallSegment(opening.start, opening.end, 0, opening.heightStart);
        }
        
        // Segmento sopra la finestra
        if (opening.heightEnd < this.height - 0.01) {
          this.createWallSegment(opening.start, opening.end, opening.heightEnd, this.height);
        }
      }
      
      // Per le porte, crea segmento sopra (architrave)
      if (opening.type === 'door') {
        if (opening.heightEnd < this.height - 0.01) {
          this.createWallSegment(opening.start, opening.end, opening.heightEnd, this.height);
        }
      }
      
      currentPos = opening.end;
    });
    
    // Segmento finale dopo l'ultima apertura
    if (currentPos < 1 - 0.01) {
      this.createWallSegment(currentPos, 1, 0, this.height);
    }
  }
  
  createWallSegment(startPos, endPos, heightStart, heightEnd) {
    const wallLength = this.getLength();
    const segmentLength = (endPos - startPos) * wallLength;
    const segmentHeight = heightEnd - heightStart;
    
    if (segmentLength < 0.01 || segmentHeight < 0.01) return;
    
    const geometry = new THREE.BoxGeometry(segmentLength, segmentHeight, this.thickness);
    const segment = new THREE.Mesh(geometry, this.material);
    segment.castShadow = false;
    segment.receiveShadow = false;
    
    // Calcola la posizione del segmento nel mondo
    const segmentCenter = (startPos + endPos) / 2;
    const worldPos = new THREE.Vector3().lerpVectors(this.start, this.end, segmentCenter);
    
    segment.position.copy(worldPos);
    segment.position.y = heightStart + segmentHeight / 2;
    
    const endPoint = this.end.clone();
    endPoint.y = segment.position.y;
    segment.lookAt(endPoint);
    segment.rotateY(-Math.PI / 2);
    
    this.mesh.add(segment);
  }
  
  updateMesh() {
    if (!this.mesh) return;
    this.rebuildWallSegments();
  }
  
  dispose() {
    if (this.mesh) {
      this.mesh.traverse((child) => {
        if (child.geometry) child.geometry.dispose();
      });
      if (this.material) this.material.dispose();
    }
  }
}

export default Wall;